import React, { useCallback, useMemo } from 'react';

import { CatalogRoom, ConflictResolution, ExtractedDateRange, StopsellRoom } from '~/types/stopsell';

import { findRateConflict, sliceResolutionsByRoom } from '~/utils/stopsellConflicts';

import StopsellRoomGroup from './StopsellRoomGroup';

interface Props {
  roomIndex: number;
  room: StopsellRoom;
  localDates: Array<ExtractedDateRange>;
  catalogRooms: Array<CatalogRoom>;
  selectedRoomTypeId: string;
  resolutions: Record<string, ConflictResolution>;
  onDateChange: (roomIndex: number, dateIndex: number, value: boolean) => void;
  onToggleAll: (roomIndex: number, value: boolean) => void;
  onRoomTypeChange: (roomIndex: number, roomTypeId: string) => void;
  onResolve: (roomIndex: number, dateId: string, rateId: string, status: ConflictResolution) => void;
  onUndo: (roomIndex: number, dateId: string, rateId: string) => void;
  onEditDate: (roomIndex: number, dateId: string, newRange: { start: string; end: string }) => void;
  onDeleteDate: (roomIndex: number, dateId: string) => void;
  onAddExtractedDate: (roomIndex: number, start: string, end: string) => void;
}

export default function StopsellRoomGroupRow({
  roomIndex,
  room,
  localDates,
  catalogRooms,
  selectedRoomTypeId,
  resolutions,
  onDateChange,
  onToggleAll,
  onRoomTypeChange,
  onResolve,
  onUndo,
  onEditDate,
  onDeleteDate,
  onAddExtractedDate,
}: Props) {
  const selectedCatalogRoom = useMemo(
    () => catalogRooms.find((c) => c.id === selectedRoomTypeId),
    [catalogRooms, selectedRoomTypeId],
  );

  const blackoutDates = useMemo(() => selectedCatalogRoom?.blackoutDates ?? [], [selectedCatalogRoom]);

  const catalogRoomRates = useMemo(() => selectedCatalogRoom?.roomRates ?? [], [selectedCatalogRoom]);

  const rateConflicts = useMemo(
    () => localDates.map((date) => catalogRoomRates.map((rate) => findRateConflict(date, rate.id, blackoutDates))),
    [localDates, catalogRoomRates, blackoutDates],
  );

  const rateResolutions = useMemo(() => sliceResolutionsByRoom(resolutions, roomIndex), [resolutions, roomIndex]);

  const handleChange = useCallback(
    (dateIndex: number, value: boolean) => onDateChange(roomIndex, dateIndex, value),
    [roomIndex, onDateChange],
  );

  const handleToggleAll = useCallback((value: boolean) => onToggleAll(roomIndex, value), [roomIndex, onToggleAll]);

  const handleRoomTypeChange = useCallback(
    (roomTypeId: string) => onRoomTypeChange(roomIndex, roomTypeId),
    [roomIndex, onRoomTypeChange],
  );

  const handleResolve = useCallback(
    (dateId: string, rateId: string, status: ConflictResolution) => onResolve(roomIndex, dateId, rateId, status),
    [roomIndex, onResolve],
  );

  const handleUndo = useCallback(
    (dateId: string, rateId: string) => onUndo(roomIndex, dateId, rateId),
    [roomIndex, onUndo],
  );

  const handleEditDate = useCallback(
    (dateId: string, newRange: { start: string; end: string }) => onEditDate(roomIndex, dateId, newRange),
    [roomIndex, onEditDate],
  );

  const handleDeleteDate = useCallback((dateId: string) => onDeleteDate(roomIndex, dateId), [roomIndex, onDeleteDate]);

  const handleAddExtractedDate = useCallback(
    (start: string, end: string) => onAddExtractedDate(roomIndex, start, end),
    [roomIndex, onAddExtractedDate],
  );

  return (
    <StopsellRoomGroup
      room={room}
      localDates={localDates}
      catalogRooms={catalogRooms}
      selectedRoomTypeId={selectedRoomTypeId}
      blackoutDates={blackoutDates}
      catalogRoomRates={catalogRoomRates}
      rateConflicts={rateConflicts}
      rateResolutions={rateResolutions}
      onChange={handleChange}
      onToggleAll={handleToggleAll}
      onRoomTypeChange={handleRoomTypeChange}
      onResolve={handleResolve}
      onUndo={handleUndo}
      onEditDate={handleEditDate}
      onDeleteDate={handleDeleteDate}
      onAddExtractedDate={handleAddExtractedDate}
    />
  );
}
